/**
 * parallaxConfig — pure silhouette geometry for the `parallax` scenery effect.
 * `silhouettePoints` returns a CLOSED filled-polygon outline (flat bottom edge,
 * shaped top edge) for a distant-scenery band: peaks / spires / columns / cone /
 * dunes / ridge. Seeded so a level's skyline is stable across reloads. Pure math
 * (no Pixi) — the builder turns these points into a camera-scrolled IGraphics fill.
 */
import { seededRand } from '../atmospherePlacement';
import type { ParallaxEffectSpec } from '../../../../config/atmosphere/effectLayer.types';

export interface Point { x: number; y: number; }
export type ParallaxShape = ParallaxEffectSpec['shape'];

/** Base top-profile sample count per shape (denser = smoother / more teeth). */
function baseSegments(shape: ParallaxShape): number {
  switch (shape) {
    case 'cone': return 8;
    case 'peaks': return 11;
    case 'spires': return 17;
    case 'columns': return 16;
    case 'stalactites': return 18;
    case 'dunes': return 20;
    case 'ridge': return 14;
    default: return 12;
  }
}

/** Profile sample count, scaled by the granular `detail` multiplier (default 1 =
 *  unchanged). Higher detail = a denser, more intricate skyline (PX1). */
function segments(shape: ParallaxShape, detail = 1): number {
  return Math.max(2, Math.round(baseSegments(shape) * detail));
}

/**
 * Top-edge depth at profile fraction t (0..1), in px below `topY` — 0 = a peak
 * touching topY, `height` = a valley at the band bottom. Shape-specific profile
 * with a seeded jitter so each instance differs.
 */
function profileDepth(shape: ParallaxShape, t: number, i: number, height: number, seed: number): number {
  const jitter = (seededRand(seed + i * 1.7) - 0.5) * 0.12; // ±6% seeded variation
  let f: number; // 0 = high peak, 1 = low valley
  switch (shape) {
    case 'cone': {
      // A few broad volcanic cones — wide V humps.
      const hump = Math.abs(Math.sin(t * Math.PI * 2.5));
      f = 0.15 + (1 - hump) * 0.7;
      break;
    }
    case 'peaks': {
      // Jagged mountain range — mid-frequency humps.
      const hump = Math.sin(t * Math.PI * 5) * 0.5 + 0.5;
      f = 0.2 + (1 - hump) * 0.6;
      break;
    }
    case 'spires': {
      // Sharp tall crystal spires — high-frequency narrow peaks.
      const sharp = Math.abs(Math.sin(t * Math.PI * 9));
      f = 0.92 - sharp * 0.82;
      break;
    }
    case 'columns': {
      // Square-topped temple columns — alternating flat high/low blocks.
      f = (Math.floor(t * 8) % 2 === 0) ? 0.12 : 0.85;
      break;
    }
    case 'stalactites': {
      // Sharp subterranean cavern teeth / stalactites hanging down.
      const sharp = Math.abs(Math.sin(t * Math.PI * 11));
      f = 0.16 + sharp * 0.78;
      break;
    }
    case 'dunes': {
      // Smooth rolling dunes — low-frequency sine.
      const wave = Math.sin(t * Math.PI * 2.2) * 0.5 + 0.5;
      f = 0.45 + (1 - wave) * 0.4;
      break;
    }
    case 'ridge':
    default: {
      // Low gentle ridge near the band bottom.
      const wave = Math.sin(t * Math.PI * 3) * 0.5 + 0.5;
      f = 0.6 + (1 - wave) * 0.3;
      break;
    }
  }
  const clamped = Math.max(0, Math.min(1, f + jitter));
  return clamped * height;
}

/**
 * A closed polygon outline for a scenery silhouette band spanning [x0, x0+width],
 * occupying y in [topY, topY+height].
 *
 * For background/foreground (default): flat bottom edge, shaped top profile.
 * For ceiling: flat top edge (skyTopY), shaped bottom profile (stalactites/roots).
 *
 * Automatically scales sampling frequency and profile cycle across wide maps
 * so physical feature scale remains consistent instead of stretching horizontally.
 */
export function silhouettePoints(
  shape: ParallaxShape,
  x0: number,
  topY: number,
  width: number,
  height: number,
  seed: number,
  detail = 1,
  plane: 'background' | 'foreground' | 'ceiling' = 'background',
): Point[] {
  const repeatFactor = Math.max(1, width / 3840);
  const n = Math.max(2, Math.round(segments(shape, detail) * repeatFactor));
  const bottom = topY + height;

  if (plane === 'ceiling') {
    const pts: Point[] = [{ x: x0, y: topY }]; // top-left
    for (let i = 0; i <= n; i += 1) {
      const t = i / n;
      const effectiveT = t * repeatFactor;
      const u = effectiveT % 1;
      const cycle = Math.floor(effectiveT);
      pts.push({ x: x0 + t * width, y: topY + profileDepth(shape, u, i, height, seed + cycle * 101) });
    }
    pts.push({ x: x0 + width, y: topY }); // top-right
    return pts;
  }

  const pts: Point[] = [{ x: x0, y: bottom }]; // bottom-left
  for (let i = 0; i <= n; i += 1) {
    const t = i / n;
    const effectiveT = t * repeatFactor;
    const u = effectiveT % 1;
    const cycle = Math.floor(effectiveT);
    pts.push({ x: x0 + t * width, y: topY + profileDepth(shape, u, i, height, seed + cycle * 101) });
  }
  pts.push({ x: x0 + width, y: bottom }); // bottom-right
  return pts;
}

/**
 * Horizontal sway offset (px) for a parallax plane at `elapsedMs` — a slow sine
 * drift so a plane breathes sideways instead of sitting dead still. Pure; returns
 * 0 when no sway is configured. Granular per-plane (PX1).
 */
export function swayOffset(elapsedMs: number, sway?: { ampPx: number; periodMs: number }): number {
  if (!sway || sway.periodMs <= 0) return 0;
  return Math.sin((elapsedMs / sway.periodMs) * Math.PI * 2) * sway.ampPx;
}
